<template>
  <IonModal :is-open="isOpen" @did-dismiss="$emit('close')">
    <IonHeader>
      <div class="date-picker-header">
        <IonTitle>{{ title || t('common.selectDateRange') }}</IonTitle>
        <IonButtons slot="end">
          <IonButton @click="$emit('close')">
            <IonIcon :icon="close" />
          </IonButton>
        </IonButtons>
      </div>
    </IonHeader>
    <div class="date-picker-content">
      <!-- Range summary -->
      <div class="range-summary">
        <div class="range-date" :class="{ active: !!selectedRange.start }">
          <span class="range-date-label">{{ startLabel || t('startDate') }}</span>
          <span class="range-date-value">
            {{ selectedRange.start ? formatDisplayDate(selectedRange.start) : '—' }}
          </span>
        </div>
        <div class="range-separator">
          <IonIcon :icon="arrowForwardOutline" />
        </div>
        <div class="range-date" :class="{ active: !!selectedRange.end }">
          <span class="range-date-label">{{ endLabel || t('endDate') }}</span>
          <span class="range-date-value">
            {{ selectedRange.end ? formatDisplayDate(selectedRange.end) : '—' }}
          </span>
        </div>
      </div>

      <!-- Duration chip -->
      <div v-if="durationText" class="duration-chip">
        <IonChip color="primary" outline>
          <IonIcon :icon="timeOutline" />
          <IonLabel>{{ durationText }}</IonLabel>
        </IonChip>
      </div>

      <!-- Calendar -->
      <VDatePicker
        v-model.range="selectedRange"
        :locale="currentLocale"
        expanded
        borderless
        class="date-picker"
      />

      <!-- Error message -->
      <div v-if="errorMessage" class="error-text">
        {{ errorMessage }}
      </div>

      <!-- Actions -->
      <div class="date-picker-actions">
        <Button @click="$emit('close')">
          {{ t('common.cancel') }}
        </Button>
        <Button type="primary" :disabled="!isRangeValid" @click="confirmSelection">
          {{ t('common.confirm') }}
        </Button>
      </div>
    </div>
  </IonModal>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  IonModal,
  IonHeader,
  IonTitle,
  IonButtons,
  IonButton,
  IonIcon,
  IonChip,
  IonLabel,
} from '@ionic/vue';
import { close, arrowForwardOutline, timeOutline } from 'ionicons/icons';
import { DatePicker as VDatePicker } from 'v-calendar';
import 'v-calendar/style.css';
import Button from '@/common/components/ui/button/Button.vue';

interface DateRange {
  start: Date | undefined;
  end: Date | undefined;
}

interface ExistingPeriod {
  startDate: string;
  endDate: string;
}

interface Props {
  isOpen: boolean;
  initialStartDate?: Date | string | null;
  initialEndDate?: Date | string | null;
  title?: string;
  startLabel?: string;
  endLabel?: string;
  existingPeriods?: ExistingPeriod[];
}

const props = withDefaults(defineProps<Props>(), {
  initialStartDate: null,
  initialEndDate: null,
  existingPeriods: () => [],
});

const emit = defineEmits<{
  close: [];
  confirm: [range: { startDate: string; endDate: string }];
}>();

const { t, locale } = useI18n();

const normalizeDate = (date: Date | string | null | undefined): Date | undefined => {
  if (!date) return undefined;
  return typeof date === 'string' ? new Date(date) : new Date(date);
};

const selectedRange = ref<any>({
  start: normalizeDate(props.initialStartDate),
  end: normalizeDate(props.initialEndDate),
});

const errorMessage = ref('');

const currentLocale = computed(() => {
  return locale.value.startsWith('fr') ? 'fr-FR' : 'en-US';
});

const isRangeValid = computed(() => {
  return selectedRange.value.start && selectedRange.value.end;
});

const hasOverlap = (newStart: Date, newEnd: Date): boolean => {
  return props.existingPeriods.some((period) => {
    const existingStart = new Date(period.startDate);
    const existingEnd = new Date(period.endDate);
    return newStart <= existingEnd && newEnd >= existingStart;
  });
};

const durationText = computed(() => {
  const { start, end } = selectedRange.value;
  if (!start || !end) return '';

  const diffTime = Math.abs(end.getTime() - start.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return `1 ${t('common.day')}`;
  if (diffDays === 1) return `1 ${t('common.day')}`;
  if (diffDays < 30) return `${diffDays} ${t('common.days')}`;
  if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    const remainingDays = diffDays % 30;
    let result = `${months} ${months === 1 ? t('common.month') : t('common.months')}`;
    if (remainingDays > 0) {
      result += ` ${remainingDays} ${remainingDays === 1 ? t('common.day') : t('common.days')}`;
    }
    return result;
  }
  const years = Math.floor(diffDays / 365);
  const remainingDays = diffDays % 365;
  let result = `${years} ${years === 1 ? t('common.year') : t('common.years')}`;
  if (remainingDays > 0) {
    const months = Math.floor(remainingDays / 30);
    if (months > 0) {
      result += ` ${months} ${months === 1 ? t('common.month') : t('common.months')}`;
    }
  }
  return result;
});

const formatDisplayDate = (date: Date): string => {
  return date.toLocaleDateString(currentLocale.value, {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
};

const toDateString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Reset when modal opens
watch(
  () => props.isOpen,
  (isOpen) => {
    if (isOpen) {
      selectedRange.value = {
        start: normalizeDate(props.initialStartDate),
        end: normalizeDate(props.initialEndDate),
      };
      errorMessage.value = '';
    }
  }
);

const confirmSelection = () => {
  const { start, end } = selectedRange.value;
  if (!start || !end) {
    errorMessage.value = t('common.pleaseSelectBothDates');
    return;
  }

  if (hasOverlap(start, end)) {
    errorMessage.value = t('common.periodsCannotOverlap');
    return;
  }

  errorMessage.value = '';
  emit('confirm', {
    startDate: toDateString(start),
    endDate: toDateString(end),
  });
};
</script>

<style scoped>
ion-header {
  --background: var(--ion-background-color);
  --color: var(--ion-text-color);
  box-shadow: none;
  border-bottom: 1px solid var(--ion-background-color-tertiary);
}

.date-picker-content {
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.date-picker-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  height: 56px;
  background-color: var(--ion-background-color);
  color: var(--ion-text-color);
}

/* Range summary */
.range-summary {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  max-width: 350px;
  padding: 12px;
  background: var(--ion-background-color-secondary);
  border-radius: 12px;
}

.range-date {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px;
  border-radius: 8px;
  transition: background 0.2s;
}

.range-date.active {
  background: var(--ion-background-color);
}

.range-date-label {
  font-size: 0.7rem;
  color: var(--ion-color-medium);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.range-date-value {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--ion-text-color);
}

.range-separator {
  color: var(--ion-color-medium);
  font-size: 1rem;
  flex-shrink: 0;
}

.duration-chip {
  display: flex;
  justify-content: center;
}

/* Calendar */
.date-picker {
  width: 100%;
  max-width: 350px;
  font-family: inherit;
}

.date-picker-actions {
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: 8px;
  width: 100%;
}

.error-text {
  color: var(--ion-color-danger);
  font-size: 0.875rem;
}

/* Override v-calendar CSS variables to match app theme */
:deep(.vc-container) {
  --vc-bg: var(--ion-background-color);
  --vc-border: var(--ion-background-color-tertiary);
  --vc-color: var(--ion-text-color);
  --vc-accent-50: var(--ion-color-primary-tint);
  --vc-accent-100: var(--ion-color-primary-tint);
  --vc-accent-200: var(--ion-color-primary-tint);
  --vc-accent-300: var(--ion-color-primary-tint);
  --vc-accent-400: var(--ion-color-primary);
  --vc-accent-500: var(--ion-color-primary);
  --vc-accent-600: var(--ion-color-primary);
  --vc-accent-700: var(--ion-color-primary-shade);
  --vc-accent-800: var(--ion-color-primary-shade);
  --vc-accent-900: var(--ion-color-primary-shade);
  --vc-gray-50: var(--ion-background-color);
  --vc-gray-100: var(--ion-background-color-secondary);
  --vc-gray-200: var(--ion-background-color-tertiary);
  --vc-gray-300: var(--ion-color-medium);
  --vc-gray-400: var(--ion-color-medium);
  --vc-gray-500: var(--ion-color-medium);
  --vc-gray-600: var(--ion-text-color);
  --vc-gray-700: var(--ion-text-color);
  --vc-gray-800: var(--ion-text-color);
  --vc-gray-900: var(--ion-text-color);
  --vc-font-family: inherit;
  background-color: var(--ion-background-color);
  border: none;
  border-radius: 12px;
}

:deep(.vc-header) {
  padding: 0px 8px;
}

:deep(.vc-title) {
  background: transparent;
  color: var(--ion-text-color);
  font-weight: 600;
}

:deep(.vc-arrow) {
  color: var(--ion-text-color);
  background: transparent;
  border-radius: 8px;
}

:deep(.vc-arrow:hover) {
  background: var(--ion-background-color-tertiary);
}

:deep(.vc-nav-arrow) {
  background: transparent;
}

:deep(.vc-nav-title) {
  background: transparent;
}

:deep(.vc-nav-item) {
  background: transparent;
  color: var(--ion-text-color);
}

:deep(.vc-weekday) {
  color: var(--ion-color-medium);
  font-weight: 500;
}

:deep(.vc-day) {
  min-height: 36px;
}

:deep(.vc-day-content) {
  color: var(--ion-text-color);
  font-weight: 400;
  border-radius: 50%;
  width: 32px;
  height: 32px;
}

:deep(.vc-day-content:hover) {
  background: var(--ion-background-color-tertiary);
}

:deep(.vc-day-content:focus) {
  background: var(--ion-background-color-tertiary);
}

:deep(.vc-highlight) {
  background: var(--ion-color-primary-tint) !important;
}

:deep(.vc-highlight-content-solid) {
  color: var(--ion-color-primary-contrast) !important;
}

:deep(.vc-day.is-today .vc-day-content) {
  border: 2px solid var(--ion-color-primary);
}

:deep(.vc-day-content.is-disabled) {
  color: var(--ion-color-medium);
  opacity: 0.5;
}
</style>
